import React from "react";
import { useNetworkStats } from "@/hooks";
import { formatAdaBigCount } from "@/lib/format";

// Splits an abbreviated count like "14.82M" into { num: "14.82", suffix: "M" }
// so the magnitude letter can be given its own accent color.
const splitSuffix = (s) => {
  const m = /^([\d,.]+)([BMK])?$/.exec(s || "");
  if (!m) return { num: s, suffix: "" };
  return { num: m[1], suffix: m[2] || "" };
};

const StatValue = ({ value }) => {
  const { num, suffix } = splitSuffix(value);
  return (
    <span className="font-heading text-xl md:text-2xl tabnum text-foreground">
      {num}
      {suffix ? <span className="text-yellow-400 text-glow ml-0.5">{suffix}</span> : null}
      {suffix ? <span className="ml-1.5 text-base text-primary/80">₳</span> : null}
    </span>
  );
};

const StatRow = ({ label, testid, children }) => (
  <div data-testid={testid} className="py-4 border-b border-primary/10 last:border-b-0">
    <div className="text-[10px] uppercase tracking-[0.25em] text-primary/70 mono mb-2">{label}</div>
    {children}
  </div>
);

const Skeleton = () => <span className="inline-block h-6 w-24 bg-primary/15 rounded-sm animate-pulse" />;

// Decorative isometric wireframe cube pair — purely a branding flourish.
const CubeIcon = () => (
  <svg viewBox="0 0 220 170" className="w-full max-w-[180px] h-auto opacity-80" fill="none">
    <g stroke="hsl(264 100% 68%)" strokeWidth="1.4" strokeLinejoin="round">
      {/* back cube */}
      <path d="M120 40 L165 60 L165 105 L120 125 L75 105 L75 60 Z" opacity="0.5" />
      <path d="M75 60 L120 80 L165 60" opacity="0.5" />
      <path d="M120 80 L120 125" opacity="0.5" />
      {/* front cube */}
      <path d="M55 85 L100 105 L100 150 L55 170 L10 150 L10 105 Z" />
      <path d="M10 105 L55 125 L100 105" />
      <path d="M55 125 L55 170" />
    </g>
  </svg>
);

// ---------- pool overview widget (home page, sits beside Top Pools) ----------
export const PoolOverviewPanel = () => {
  const { stats } = useNetworkStats(60000);
  const loading = !stats;

  return (
    <div
      data-testid="pool-overview"
      className="relative overflow-hidden border border-primary/25 bg-card/30 backdrop-blur-md rounded-2xl box-glow px-5 py-5 flex flex-col h-full"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
      <div className="text-[11px] uppercase tracking-[0.25em] text-primary mono mb-1">
        pool overview
      </div>

      <StatRow label="Total Live Stake" testid="overview-live-stake">
        {loading ? <Skeleton /> : <StatValue value={formatAdaBigCount(stats.active_stake_total)} />}
      </StatRow>
      <StatRow label="Total Pledge" testid="overview-total-pledge">
        {loading ? <Skeleton /> : <StatValue value={formatAdaBigCount(stats.total_pledge)} />}
      </StatRow>
      <StatRow label="Total Pools" testid="overview-total-pools">
        {loading ? (
          <Skeleton />
        ) : (
          <span className="font-heading text-xl md:text-2xl tabnum text-foreground">
            {stats.pools_count.toLocaleString("en-US")}
          </span>
        )}
      </StatRow>
      <StatRow label="Total Blocks (Epoch)" testid="overview-total-blocks">
        {loading ? (
          <Skeleton />
        ) : (
          <span className="font-heading text-xl md:text-2xl tabnum text-foreground">
            {stats.blocks_current_epoch.toLocaleString("en-US")}
          </span>
        )}
      </StatRow>

      <div className="mt-auto pt-6 flex justify-center">
        <CubeIcon />
      </div>
    </div>
  );
};
